import { type CaseDto, type CaseTimelineEventDto, type TimelineGroup, type TimelineTone, todayInWarsaw } from '@bqcrm/shared';
import { Anchor, Button, Group, Text, ThemeIcon, Timeline } from '@mantine/core';
import { IconCash, IconClockHour4, IconFileText, IconFlag, IconPaperclip, IconSignature, IconTool } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { dayLong, timeOf } from '../../format';
import { NextAction } from '../CasesPage';

const ICONS: Record<TimelineGroup, ReactNode> = {
  sprawa: <IconFlag size={14} />,
  oferta: <IconFileText size={14} />,
  umowa: <IconSignature size={14} />,
  finanse: <IconCash size={14} />,
  realizacja: <IconTool size={14} />,
  dokument: <IconPaperclip size={14} />,
};
const COLORS: Record<TimelineTone, string> = { info: 'gray', ok: 'eko', warn: 'orange', bad: 'red' };

const fmtDay = (iso: string) => dayLong(iso, { weekday: false });
const fmtTime = timeOf;
/** dzień zdarzenia w czasie polskim (zdarzenia z samą datą — data bez przeliczania strefy) */
const dayOf = (e: CaseTimelineEventDto) => (e.dateOnly ? e.at.slice(0, 10) : todayInWarsaw(new Date(e.at)));

/** Zakładka „Oś czasu”: najważniejsze kroki sprawy od założenia do dziś, od góry do dołu, z linkami do PDF. */
export function TimelineTab({ c }: { c: CaseDto }) {
  const q = useQuery({ queryKey: ['case-timeline', c.id], queryFn: () => api<CaseTimelineEventDto[]>(`/cases/${c.id}/timeline`) });
  const events = q.data ?? [];
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Timeline active={events.length - 1} bulletSize={26} lineWidth={2} color="eko" mt="xs">
        {events.map((e, i) => {
          const day = dayOf(e);
          const newDay = i === 0 || dayOf(events[i - 1]!) !== day;
          return (
            <Timeline.Item
              key={`${e.at}-${i}`}
              lineVariant="solid"
              bullet={
                <ThemeIcon size={26} radius="xl" color={COLORS[e.tone]} variant={e.tone === 'info' ? 'light' : 'filled'}>
                  {ICONS[e.group]}
                </ThemeIcon>
              }
              title={
                <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start">
                  <Text size="sm" fw={600} style={{ minWidth: 0 }}>
                    {e.route ? (
                      <Anchor component={Link} to={e.route} inherit c="inherit" underline="hover">
                        {e.title}
                      </Anchor>
                    ) : (
                      e.title
                    )}
                  </Text>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} title={fmtDay(e.at)}>
                    {newDay ? fmtDay(day) : ''}
                    {!e.dateOnly && `${newDay ? ', ' : ''}${fmtTime(e.at)}`}
                  </Text>
                </Group>
              }
            >
              {e.detail && (
                <Text size="xs" c="dimmed" mt={2}>
                  {e.detail}
                </Text>
              )}
              {e.files.length > 0 && (
                <Group gap={6} mt={6}>
                  {e.files.map((f) => (
                    <Button key={f.href} component="a" href={f.href} target="_blank" rel="noopener" size="compact-xs" variant="light" color="gray" leftSection={<IconFileText size={12} />}>
                      {f.label}
                    </Button>
                  ))}
                </Group>
              )}
            </Timeline.Item>
          );
        })}
        {!c.closedAt && (
          <Timeline.Item
            lineVariant="dashed"
            bullet={
              <ThemeIcon size={26} radius="xl" color="blue" variant="light">
                <IconClockHour4 size={14} />
              </ThemeIcon>
            }
            title={
              <Text size="sm" fw={600}>
                Teraz
              </Text>
            }
          >
            <NextAction next={c.next} c={c} />
          </Timeline.Item>
        )}
      </Timeline>
      {!events.length && <EmptyState compact title="Brak zdarzeń" hint="Tu pojawią się oferty, umowy, faktury i protokoły sprawy." />}
    </QueryState>
  );
}
